import { IPaymentDetails } from "../../s25-payments/payment.service";
import { S25PricingUtil } from "../../s25.pricing.util";
import { S25Datefilter } from "../../../s25-dateformat/s25.datefilter.service";
import { S25DateParse } from "../../../../util/core/s25.date.parse";
import { Table } from "../../../s25-table/Table";

export namespace PaymentRows {
    const VOIDABLE_SOURCES = new Set(["manual", "stripe", "sevenPoint"]);

    export type PaymentAmountEntry = { value: number; type: "amount" | "percent" };

    /**
     * What a draft amount actually charges. A percentage is resolved against the outstanding balance
     * in cents, so a third of a balance cannot land a fraction of a cent off.
     */
    export function paymentAmountDollars(entry: PaymentAmountEntry, balance: number): number {
        if (entry.type === "amount") return entry.value;
        return S25PricingUtil.convertToDollars(
            Math.round(S25PricingUtil.convertToCents(balance) * (entry.value / 100)),
        );
    }

    /** FINAL is stored on the payment but has always read as BALANCE in the UI. */
    export function paymentTypeLabel(type: IPaymentDetails["type"]): string {
        return type === "FINAL" ? "BALANCE" : type;
    }

    /** How a processor reads to a user; the stored ids are camelCase service names. */
    export function paymentSourceLabel(source: string | undefined): string {
        if (!source) return "";
        if (source === "sevenPoint") return "7 Point Solutions";
        return source[0]!.toUpperCase() + source.slice(1);
    }

    /** One payment, formatted for the history table. */
    export type PaymentRowModel = {
        rowId: string;
        payment: IPaymentDetails;
        paymentDetailId: number;
        createdDate: string;
        dueDate: string;
        source: string;
        description: string;
        amount: string;
        typeLabel: string;
        statusText: string;
        voidText: string;
        notes: string;
        isVoid: boolean;
        /** A manual payment still awaiting money can be settled by hand. */
        canMarkPaid: boolean;
        canVoid: boolean;
        /** A live payment can be chased with a reminder email. */
        canRemind: boolean;
    };

    export type PaymentRowOptions = { dateFormat: string | undefined; viewOnly: boolean };

    function formatDate(value: string | undefined, dateFormat: string | undefined): string {
        if (!value) return "";
        return S25Datefilter.transform(S25DateParse.parseDropTZLenient(value), dateFormat ?? "") ?? "";
    }

    /**
     * How a payment reads in the Status column. A settled payment carries its own audit trail - who
     * paid it, when, and against what confirmation.
     */
    function statusText(payment: IPaymentDetails, dateFormat: string | undefined): string {
        const status = payment.paymentStatus ?? "";
        if (payment.isVoid || status !== "paid") return status;

        return [
            status,
            payment.customerId && `by ${payment.customerId}`,
            payment.paymentSuccess && `on ${formatDate(payment.paymentSuccess, dateFormat)}`,
            payment.authorizationId && `with confirmation ${payment.authorizationId}`,
        ]
            .filter(Boolean)
            .join(" ");
    }

    function voidText(payment: IPaymentDetails, dateFormat: string | undefined): string {
        if (!payment.isVoid) return "";

        return [
            "Yes",
            payment.voidUsername && `by ${payment.voidUsername}`,
            payment.voidDate && `on ${formatDate(payment.voidDate, dateFormat)}`,
            payment.voidReason && `because: ${payment.voidReason}`,
        ]
            .filter(Boolean)
            .join(" ");
    }

    export function paymentRowModels(payments: IPaymentDetails[], options: PaymentRowOptions): PaymentRowModel[] {
        return payments.map((payment) => {
            const isVoid = !!payment.isVoid;
            const isManualUnpaid = payment.source === "manual" && payment.paymentStatus !== "paid";

            return {
                rowId: `payment-${payment.paymentDetailId}`,
                payment,
                paymentDetailId: payment.paymentDetailId,
                createdDate: formatDate(payment.paymentCreated, options.dateFormat),
                dueDate: formatDate(payment.dueDate, options.dateFormat),
                source: paymentSourceLabel(payment.source),
                description: payment.productDescription ?? "",
                amount: S25PricingUtil.formatCurrency((payment.amountTotalCents ?? 0) / 100),
                typeLabel: paymentTypeLabel(payment.type),
                statusText: statusText(payment, options.dateFormat),
                voidText: voidText(payment, options.dateFormat),
                notes: payment.notes ?? "",
                isVoid,
                canMarkPaid: !options.viewOnly && !isVoid && isManualUnpaid,
                canVoid: !options.viewOnly && !isVoid && VOIDABLE_SOURCES.has(payment.source ?? ""),
                canRemind: !isVoid,
            };
        });
    }

    export const PAYMENT_COLUMN_IDS = [
        "paymentId",
        "created",
        "due",
        "source",
        "description",
        "amount",
        "type",
        "status",
        "void",
        "notes",
    ] as const;

    export function paymentColumns(): Table.Column[] {
        return [
            { header: "ID", id: "paymentId", width: 70 },
            { header: "Create Date", id: "created" },
            { header: "Due Date", id: "due" },
            { header: "Source", id: "source" },
            { header: "Description", id: "description" },
            { header: "Amount", id: "amount" },
            { header: "Type", id: "type" },
            { header: "Status", id: "status" },
            { header: "Void", id: "void" },
            { header: "Notes", id: "notes" },
        ];
    }
}
